/* eslint-disable lit-a11y/click-events-have-key-events */
import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * Expand Card for Divertissement Pack component. Used in new EDISON view.
 *
 * @prop title The title of the card.
 * @prop subtitle The subtitle of the card (optional).
 * @prop inputName The name of the form input (optional).
 * @prop imageUrl The URL to image to display in the content (optional).
 * @prop imageSize The image width size in pixel.
 * @prop price The price to display (optional).
 * @prop isPromo Display a "Promo" badge instead of price (optional).
 * @prop isFree Display a "Offert" badge instead of price (optional).
 * @prop isIncluded Display a "inclus" badge instead of price (optional).
 * @prop frequency The text next to the price (optional).
 * @prop state The state of card: 'default' | 'add' | 'update' | 'remove'.
 * @prop context The context type (optional).
 * @slot card-content The content inside the expand card.
 */
@customElement('nn-child-card')
export class ChildCard extends LitElement {
  static styles = css`
    body {
      margin: 0;
    }

    .child-card {
      align-items: flex-start;
      background: var(--nn-white, #fff);
      border: 1px solid var(--nn-black-29, #e8e8e8);
      border-radius: 16px;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      font-family: 'font-medium';
      gap: 12px;
      justify-content: start;
      padding: 10px;
      text-align: left;
      transition: all ease-in-out var(--nn-transition, 0.3s);
    }

    .child-card--default,
    .child-card--remove {
      cursor: pointer;
    }

    .child-card--default:hover,
    .child-card--remove:hover {
      border-color: var(--nn-odil-blue, #598be7);
    }

    .child-card--add {
      border: 2px solid var(--nn-odil-blue, #598be7);
      padding: 10px;
    }

    .child-card--update {
      background: var(--nn-black-12, #f7f8fb);
    }

    .child-card__top {
      margin-bottom: 12px;
    }

    .child-card__title {
      font-family: 'font-heavy';
      font-size: 12px;
      line-height: 1.58;
      margin-top: auto;
      margin-bottom: auto;
    }

    .child-card__subtitle {
      font-family: 'font-light';
      font-size: 11px;
      line-height: 1.58;
      margin-top: auto;
      margin-bottom: auto;
    }

    .child-card__wrapper {
      align-items: flex-start;
      display: flex;
      flex-flow: row wrap;
      gap: 5%;
      width: 100%;
    }

    .child-card__top-left {
      flex: 1 1 45%;
      order: 1;
    }

    .child-card__top-right {
      align-items: flex-start;
      display: flex;
      flex-basis: 45%;
      flex-direction: column;
      order: 2;
    }

    .child-card__bottom {
      flex-basis: 100%;
      margin-top: 10px;
      max-width: 100%;
      order: 3;
    }

    .child-card__price {
      border-radius: 8px;
      font-family: 'font-heavy';
      font-size: 12px;
      line-height: 1.58;
      margin: 0;
      min-width: 53px;
      text-align: center;
      white-space: nowrap;
    }

    .child-card__sub-price {
      font-family: 'font-light';
      font-size: 10px;
      line-height: 12px;
      margin: 0;
      text-align: center;
      white-space: nowrap;
    }

    .child-card__content {
      color: var(--nn-grey-3, #494949);
      font-family: 'font-light';
      font-size: 11px;
      line-height: 1.51;
      min-height: 54px;
    }
  `;

  @property({ type: String }) title = 'Title';

  @property({ type: String }) subtitle = null;

  @property({ type: String }) inputName = (Math.random() + 1)
    .toString(36)
    .substring(7);

  @property({ type: String }) imageUrl = '';

  @property({ type: String }) imageSize = '60px';

  @property({ type: String }) price = null;

  @property({ type: String }) subPrice = null;

  @property({ type: Boolean }) isFree = false;

  @property({ type: Boolean }) isIncluded = false;

  @property({ type: String }) frequency = null;

  @property({ type: Boolean }) isPromo = false;

  @property({ type: String }) state = 'default';

  renderImage() {
    if (this.imageUrl) {
      return html`
        <img
          class="${this.id}__image"
          width="${this.imageSize}"
          src="${this.imageUrl}"
          alt=""
        />
      `;
    }
    return html``;
  }

  renderPromoBadge = () =>
    html`
      <nn-badge
        class="child-card__price"
        style="margin-bottom: 6px;"
        text="Promo"
        variant="light"
      ></nn-badge>
    `;

  renderSubPrice() {
    if (this.subPrice && this.frequency) {
      return html`
        <span class="child-card__sub-price">
          ${this.subPrice}<span class="child-card__frequency"
            >/ ${this.frequency}</span
          >
        </span>
      `;
    }
    if (this.subPrice) {
      return html` <span class="child-card__sub-price">${this.price}</span>`;
    }
    return '';
  }

  renderPrice() {
    if (this.isFree) {
      return html`
        <nn-badge
          class="child-card__price"
          text="Offert"
          variant="pink"
        ></nn-badge>
      `;
    }
    if (this.isIncluded) {
      return html`
        <nn-badge
          class="child-card__price"
          text="Inclus"
          variant="dark"
        ></nn-badge>
      `;
    }
    if (this.price && this.frequency) {
      return html`
        ${this.isPromo ? this.renderPromoBadge() : ''}
        <p class="child-card__price">
          ${this.price}<span class="child-card__frequency"
            >/ ${this.frequency}</span
          >
        </p>
        ${this.renderSubPrice()}
      `;
    }
    if (this.price) {
      return html` <p class="child-card__price">${this.price}</p>`;
    }
    return html``;
  }

  render() {
    return html`
      <label
        class="child-card child-card--${this.state}"
        for="${this.inputName}"
        @click="${this._dispatchCardClick}"
      >
        <div class="child-card__wrapper">
          <div class="child-card__top-left">
            <p class="child-card__title">${this.title}</p>
            <p class="child-card__subtitle">${this.subtitle}</p>
          </div>
          <div class="child-card__top-right">${this.renderPrice()}</div>
          <div class="child-card__bottom">
            <slot class="child-card__content" name="child-card-content"></slot>
          </div>
        </div>
      </label>
    `;
  }

  _dispatchCardClick(e: Event) {
    if (this.state === 'default' || this.state === 'remove') {
      e.preventDefault();
      const event = new CustomEvent('onCardClick');
      this.dispatchEvent(event);
    }
  }
}
